import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload, Trash2, Shield, Users, Loader, ArrowLeft } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Labs admin — bulk student whitelist upload.
 * Only visible to users whose email is marked `is_admin: true` in
 * `labs_whitelist`. Any non-admin hitting this page sees the Access panel.
 */
export default function LabsAdminPage() {
  const navigate = useNavigate();
  const [me, setMe] = useState({ loading: true, is_admin: false });
  const [list, setList] = useState([]);
  const [bulkText, setBulkText] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [addEmail, setAddEmail] = useState('');
  const [addAdmin, setAddAdmin] = useState(false);

  useEffect(() => {
    fetch(`${API_URL}/api/labs/whoami`, { credentials: 'include' })
      .then(r => r.json())
      .then(info => {
        setMe({ loading: false, ...(info || {}) });
        if (info?.is_admin) load();
      })
      .catch(() => setMe({ loading: false, is_admin: false }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = async () => {
    const r = await fetch(`${API_URL}/api/labs/admin/whitelist`, { credentials: 'include' });
    if (r.ok) setList(await r.json());
  };

  const onBulk = async () => {
    if (!bulkText.trim()) return;
    setBusy(true); setMsg('');
    try {
      const r = await fetch(`${API_URL}/api/labs/admin/whitelist/bulk`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: bulkText, is_student: true }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || 'Failed');
      setMsg(`✅ Added ${j.added} · Updated ${j.updated} · Skipped ${j.skipped_invalid?.length || 0}`);
      setBulkText('');
      await load();
    } catch (e) {
      setMsg(`❌ ${e.message}`);
    }
    setBusy(false);
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    setBulkText(prev => prev ? `${prev}\n${text}` : text);
    e.target.value = '';
  };

  const onAddOne = async () => {
    if (!addEmail.trim()) return;
    setBusy(true); setMsg('');
    try {
      const r = await fetch(`${API_URL}/api/labs/admin/whitelist`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: addEmail.trim(), is_student: true, is_admin: addAdmin }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || 'Failed');
      setMsg(`✅ Added ${addEmail}`);
      setAddEmail(''); setAddAdmin(false);
      await load();
    } catch (e) { setMsg(`❌ ${e.message}`); }
    setBusy(false);
  };

  const onRemove = async (email) => {
    if (!window.confirm(`Remove ${email}?`)) return;
    const r = await fetch(`${API_URL}/api/labs/admin/whitelist/${encodeURIComponent(email)}`, {
      method: 'DELETE', credentials: 'include',
    });
    if (r.ok) await load();
    else { const j = await r.json().catch(() => ({})); alert(j.detail || 'Failed'); }
  };

  if (me.loading) return <div style={{ padding: '2rem', color: 'hsl(var(--muted-foreground))' }}>Loading…</div>;
  if (!me.is_admin) {
    return (
      <div style={{ padding: '2rem', maxWidth: 640, margin: '2rem auto' }}>
        <div style={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 12, padding: '1.5rem' }}>
          <h2 style={{ marginTop: 0 }}>Labs admin</h2>
          <p style={{ color: 'hsl(var(--muted-foreground))' }}>Access restricted to labs administrators.</p>
          <button onClick={() => navigate('/build')} style={{ marginTop: '1rem', padding: '0.5rem 1rem', borderRadius: 8, cursor: 'pointer' }}>Back to Build</button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '2rem', maxWidth: 1000, margin: '0 auto' }} data-testid="labs-admin-page">
      <button onClick={() => navigate('/build')} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'transparent', border: 'none', color: 'hsl(var(--muted-foreground))', cursor: 'pointer', marginBottom: '1rem' }}>
        <ArrowLeft size={16} /> Back to Build
      </button>

      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem' }}>
        <Users size={24} style={{ color: 'hsl(var(--primary))' }} />
        <h1 style={{ margin: 0, fontSize: '1.5rem' }}>Student whitelist</h1>
      </div>

      {/* Bulk paste / upload */}
      <section style={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 12, padding: '1.25rem', marginBottom: '1rem' }}>
        <h3 style={{ marginTop: 0, fontSize: '1rem' }}>Bulk add from Excel / CSV</h3>
        <p style={{ fontSize: '0.8125rem', color: 'hsl(var(--muted-foreground))', marginTop: 0 }}>
          Paste one email per line, or upload the CSV export of your classroom roster. Any token that isn't an email is skipped.
        </p>
        <textarea
          value={bulkText}
          onChange={(e) => setBulkText(e.target.value)}
          rows={6}
          placeholder={"alice@uni.edu\nbob@uni.edu\ncarol@uni.edu"}
          data-testid="bulk-text"
          style={{ width: '100%', padding: '0.75rem', fontFamily: 'inherit', fontSize: '0.875rem', borderRadius: 8, border: '1px solid hsl(var(--border))', background: 'hsl(var(--background))', color: 'hsl(var(--foreground))' }}
        />
        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem', alignItems: 'center' }}>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '0.4rem 0.9rem', border: '1px solid hsl(var(--border))', borderRadius: 8, cursor: 'pointer', fontSize: '0.8125rem' }}>
            <Upload size={14} /> Upload CSV
            <input type="file" accept=".csv,.txt,text/csv,text/plain" onChange={onFile} style={{ display: 'none' }} data-testid="bulk-csv-file" />
          </label>
          <button onClick={onBulk} disabled={busy || !bulkText.trim()} data-testid="bulk-submit"
            style={{ padding: '0.4rem 1rem', background: 'hsl(var(--primary))', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, fontSize: '0.8125rem', cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.6 : 1 }}>
            {busy ? <Loader size={14} className="spin" /> : 'Add all'}
          </button>
          {msg && <span style={{ fontSize: '0.8125rem', color: 'hsl(var(--muted-foreground))' }}>{msg}</span>}
        </div>
      </section>

      {/* Single add */}
      <section style={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 12, padding: '1.25rem', marginBottom: '1rem' }}>
        <h3 style={{ marginTop: 0, fontSize: '1rem' }}>Add one email</h3>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <input value={addEmail} onChange={(e) => setAddEmail(e.target.value)} placeholder="student@uni.edu" type="email"
            data-testid="single-email-input"
            style={{ flex: '1 1 220px', padding: '0.5rem 0.75rem', borderRadius: 8, border: '1px solid hsl(var(--border))', background: 'hsl(var(--background))', color: 'hsl(var(--foreground))' }} />
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.8125rem', color: 'hsl(var(--muted-foreground))' }}>
            <input type="checkbox" checked={addAdmin} onChange={(e) => setAddAdmin(e.target.checked)} data-testid="single-is-admin" />
            Admin
          </label>
          <button onClick={onAddOne} disabled={busy || !addEmail.trim()} data-testid="single-add-btn"
            style={{ padding: '0.4rem 1rem', background: 'hsl(var(--primary))', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, fontSize: '0.8125rem', cursor: 'pointer' }}>Add</button>
        </div>
      </section>

      {/* Current list */}
      <section style={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 12, padding: '1.25rem' }}>
        <h3 style={{ marginTop: 0, fontSize: '1rem' }}>Current whitelist ({list.length})</h3>
        {list.length === 0 ? (
          <p style={{ fontSize: '0.8125rem', color: 'hsl(var(--muted-foreground))' }}>No entries yet.</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', fontSize: '0.8125rem', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid hsl(var(--border))', textAlign: 'left', color: 'hsl(var(--muted-foreground))' }}>
                  <th style={{ padding: '0.5rem' }}>Email</th>
                  <th style={{ padding: '0.5rem' }}>Role</th>
                  <th style={{ padding: '0.5rem' }}>Added</th>
                  <th style={{ padding: '0.5rem' }}>By</th>
                  <th style={{ padding: '0.5rem' }}></th>
                </tr>
              </thead>
              <tbody>
                {list.map(e => (
                  <tr key={e.email} style={{ borderBottom: '1px solid hsl(var(--border) / 0.5)' }} data-testid={`row-${e.email}`}>
                    <td style={{ padding: '0.5rem', fontWeight: 500 }}>{e.email}</td>
                    <td style={{ padding: '0.5rem' }}>
                      {e.is_admin
                        ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: 'hsl(var(--primary))' }}><Shield size={12} /> Admin</span>
                        : <span style={{ color: 'hsl(var(--muted-foreground))' }}>Student</span>}
                    </td>
                    <td style={{ padding: '0.5rem', color: 'hsl(var(--muted-foreground))' }}>{e.added_at ? new Date(e.added_at).toLocaleDateString() : '—'}</td>
                    <td style={{ padding: '0.5rem', color: 'hsl(var(--muted-foreground))' }}>{e.added_by || '—'}</td>
                    <td style={{ padding: '0.5rem', textAlign: 'right' }}>
                      <button onClick={() => onRemove(e.email)} data-testid={`remove-${e.email}`}
                        style={{ background: 'transparent', border: 'none', color: 'hsl(var(--muted-foreground))', cursor: 'pointer' }}>
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
